import React from "react";
import useDroneGame from "./hooks/useDroneGame";
import DroneDash from "./DroneDash";
import DroneDashLeaderboard from "./components/DroneDashLeaderboard";
import DroneDashHistory from "./components/DroneDashHistory";
import DroneDashStreak from "./components/DroneDashStreak";
import DroneDashHelp from "./components/DroneDashHelp";
import Gumball from "../gumball/Gumball"; 
import GumballHistoryPage from "../components/GumballHistoryPage"; 

export default function DroneDashHome() {
  const game = useDroneGame();

  if (game.screen === "game") {
    return (
      <DroneDash
        difficulty={game.difficulty}
        mode={game.mode}
        onGameOver={(result) => {
          game.endGame(result);
        }}
        onExit={() => game.setScreen("home")}
      />
    );
  }

  if (game.screen === "results") {
    return <ResultsScreen game={game} />;
  }

  if (game.screen === "leaderboard") {
    return <DroneDashLeaderboard game={game} onClose={() => game.setScreen("home")} />;
  }

  if (game.screen === "history") {
    return <DroneDashHistory game={game} onClose={() => game.setScreen("home")} />;
  }

  if (game.screen === "streak") {
    return <DroneDashStreak game={game} onClose={() => game.setScreen("home")} />;
  }

  if (game.screen === "help") {
    return <DroneDashHelp onClose={() => game.setScreen("home")} />;
  }

  if (game.screen === "gumball") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <Gumball
          gameKey="dronedash"
          history={(game.history || []).map((row) => ({ totalScore: row.score || 0 }))}
          streakInfo={{ currentStreak: game.streak }}
          onClose={() => game.setScreen("home")}
        />
      </div>
    );
  }

  if (game.screen === "rewardhistory") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <GumballHistoryPage onClose={() => game.setScreen("home")} />
      </div>
    );
  }

  return <DroneDashHomeScreen game={game} />;
}

function ResultsScreen({ game }) {
  const r = game.lastResult;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6">
      <div className="w-full max-w-sm rounded-3xl bg-gradient-to-br from-sky-500 via-cyan-500 to-emerald-500 p-[2px]">
        <div className="rounded-3xl bg-slate-950 p-6 text-center">
          <div className="text-5xl">{r?.isNewRecord ? "🏆" : "🚁"}</div>
          <div className="mt-2 text-xl font-black text-white">
            {r?.isNewRecord ? "New personal best!" : "Flight complete"}
          </div>
          <div className="mt-1 text-4xl font-black text-cyan-300">{r?.score ?? 0}</div>
          <div className="mt-1 text-sm text-slate-400">{r?.packagesCollected ?? 0} parcels delivered</div>

          <div className="mt-5 flex flex-col gap-2">
            <button
              onClick={() => game.startGame()}
              className="rounded-xl bg-sky-500 text-white font-semibold py-2.5"
            >
              Fly again
            </button>
            <button
              onClick={() => game.setScreen("home")}
              className="rounded-xl bg-slate-800 text-white py-2.5"
            >
              Back to hangar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DroneDashHomeScreen({ game }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4 py-2 flex flex-col items-center gap-5">
      <div className="text-center">
        <div className="text-5xl">🛰</div>
        <h1 className="mt-1 text-2xl font-black text-white">DroneDash</h1>
        <p className="text-sm text-slate-400">Dodge, deliver, don't run out of charge.</p>
      </div>

      <div className="w-full max-w-sm rounded-2xl bg-slate-900/80 border border-slate-800 p-4">
        <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Difficulty</div>
        <div className="grid grid-cols-3 gap-2">
          {["easy", "medium", "hard"].map((k) => (
            <button
              key={k}
              onClick={() => game.setDifficulty(k)}
              className={`rounded-xl py-1 text-sm font-semibold capitalize transition-colors ${
                game.difficulty === k
                  ? "bg-sky-500 text-white"
                  : "bg-slate-800 text-slate-300 hover:bg-slate-700"
              }`}
            >
              {k}
            </button>
          ))}
        </div>

        <div className="text-xs uppercase tracking-wide text-slate-400 mt-2 mb-2">Mode</div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => game.setMode("peace")}
            className={`rounded-xl py-1 text-sm font-semibold transition-colors ${
              game.mode === "peace" ? "bg-emerald-500 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
          >
            🕊 Peace
          </button>
          <button
            onClick={() => {
              if (game.difficulty === "easy") return;
              game.setMode("conflict");
            }}
            disabled={game.difficulty === "easy"}
            className={`rounded-xl py-1 text-sm font-semibold transition-colors ${
              game.mode === "conflict" ? "bg-rose-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            } ${game.difficulty === "easy" ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            ⚔️ Conflict
          </button>
        </div>
        {game.difficulty === "easy" && (
          <p className="mt-1 text-[11px] text-slate-500">Conflict mode unlocks on Medium or Hard.</p>
        )}
      </div>

      <button
        onClick={() => game.startGame()}
        className="w-full max-w-sm rounded-2xl bg-gradient-to-r from-sky-500 via-cyan-500 to-emerald-500 py-1 text-white font-black text-lg shadow-lg active:scale-95 transition-transform"
      >
        🛫 Take Off
      </button>

      <div className="w-full max-w-xl grid grid-cols-3 gap-3">
        <NavButton icon="👑" label="Leaderboard" onClick={() => { game.fetchLeaderboard(); game.setScreen("leaderboard"); }} />
        <NavButton icon="📈" label="History" onClick={() => { game.fetchHistory(); game.setScreen("history"); }} />
        <NavButton icon="🔥" label="Streak" onClick={() => game.setScreen("streak")} />
        <NavButton icon="🍬" label="Gumball" onClick={() => game.setScreen("gumball")} />
        <NavButton icon="📜" label="Rewards" onClick={() => game.setScreen("rewardhistory")} />
        <NavButton icon="❓" label="Help" onClick={() => game.setScreen("help")} />
      </div>
    </div>
  );
}

function NavButton({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="rounded-2xl bg-slate-900 border border-slate-800 px-2 py-1 active:scale-95 transition-transform"
    >
      <div className="text-xl">{icon}</div>
      <div className="mt-1 text-xs font-semibold text-white">{label}</div>
    </button>
  );
}
